<template>
    <dashboard v-if="student" class="student-detail-view">
        <heading>
            <h1 slot="title">{{ student.first_name }} {{ student.last_name }}</h1>

            <app-tag v-if="student.deleted_at" 
                     slot="tag" 
                     type="danger" 
                     size="small">Archived</app-tag>
            <app-tag v-if="student.at_risk" 
                     slot="tag" 
                     type="danger" 
                     size="small">At Risk</app-tag>
            <app-tag v-if="needsAppUpdate" 
                     slot="tag" 
                     type="danger" 
                     size="small">Needs App Update</app-tag>
            <app-tag
                v-if="wrongLocationServices"
                slot="tag"
                type="danger"
                size="small">Check Location Services</app-tag>
            <app-tag
                v-if="wrongBluetoothPermission"
                slot="tag"
                type="danger"
                size="small">Check Bluetooth Permission</app-tag>

            <div slot="buttons">
                <app-button size="small" @click="showEditStudent = true">Edit Student</app-button>
            </div>
        </heading>
        <info-bar>
            <div class="info-bar-column">
                <span class="title">APA Week</span>
                <span>{{ apaFormat(student.weekly_apa) }}</span>
            </div>
            <div class="info-bar-column">
                <span class="title">APA Semester</span>
                <span>{{ apaFormat(student.semester_apa) }}</span>
            </div>
            <div class="info-bar-column">
                <span class="title">Token</span>
                <span class="reset-padding">{{ student.token || "Student Active" }}</span>
                <app-button
                    class="reset-token"
                    size="mini"
                    icon="icon-refresh"
                    aria-label="Reset Token"
                    @click="handleResetToken"/>
            </div>
            <div class="info-bar-column">
                <span class="title">Email</span>
                <span>{{ student.email || "None" }}</span>
            </div>
            <div v-if="student.phone" class="info-bar-column">
                <span class="title">Phone</span>
                <span>{{ phoneFormat(student.phone) }}</span>
            </div>
            <div class="info-bar-column">
                <span class="title">
                    Last Seen 
                    <app-tooltip placement="top">
                        <div slot="content">
                            The last time any activity has been
                            <br>recorded for the student. This includes
                            <br>beacon activity and app opens.
                        </div>
                        <i class="icon icon-info"/>
                    </app-tooltip>
                </span>
                <span v-if="student.last_seen">{{ [student.last_seen + '-00:00'] | moment("from") }}</span>
                <span v-else>Never</span>
            </div>
        </info-bar>

        <row>
            <link-list :routes="routes"/>
            <router-view :student="student"/>
        </row>

        <modal v-model="showEditStudent" size="small">
            <student-edit-view :id="student.id" v-model="showEditStudent"/>
        </modal>
    </dashboard>
</template>

<script>
import semver from 'semver'

import { get, comb, phoneFormat, apaFormat } from '@/utilities'

import Student from '@/store/models/Student'

import InfoBar from '../../ui/InfoBar'
import ValueTable from '../../ui/ValueTable'
import StudentEditView from './StudentEditView'
import CourseTable from '../Course/CourseTable'

export default {
    name: 'page-students-detail',
    components: {
        InfoBar,
        ValueTable,
        StudentEditView,
        CourseTable
    },
    props: ['uuid'],
    head: {
        title() {
            return {
                inner: comb(
                    'Student',
                    '-',
                    get(this, 'student.first_name'),
                    get(this, 'student.last_name')
                )
            }
        }
    },
    data() {
        return {
            showEditStudent: false,
            routes: [
                {
                    title: 'Courses',
                    name: 'students.detail.courses',
                    icon: 'icon-courses'
                },
                {
                    title: 'Events',
                    name: 'students.detail.events',
                    icon: 'icon-events'
                },
                {
                    title: 'Information',
                    name: 'students.detail.information',
                    icon: 'icon-students'
                }
            ]
        }
    },
    computed: {
        student() { return Student.findUuid(this.uuid) },
        week() {
            return this.$store.state.params.week
        },
        needsAppUpdate() {
            if (!this.student.device) {
                return false
            }

            let type = get(this, 'student.device.type', 'ios')
            let version = semver.valid(get(this, 'student.device.app_version'))

            if (version == null) {
                return true
            }

            if (type == 'ios') {
                return !semver.satisfies(version, '>=4.0.7')
            }
            else {
                return !semver.satisfies(version, '>3.1.6')
            }
        },
        wrongLocationServices() {
            if (!this.student.device) {
                return false
            }
            let type = get(this, 'student.device.type', 'ios')
            let location = get(this, 'student.device.location_permission')
            return type == 'ios' && location != 2
        },
        wrongBluetoothPermission() {
            if (!this.student.device) {
                return false
            }
            let type = get(this, 'student.device.type', 'ios')
            let version = get(this, 'student.device.os_version')
            let bluetooth = get(this, 'student.device.bluetooth_permission')
            return type == 'ios' 
                && bluetooth != 2
                && version >= '13.0'
        },
    },
    mounted() {
        this.getStudent()
    },
    methods: {
        getStudent() {
            Student.$one({ uuid: this.uuid, week: this.week })
                .then(() => {
                    this.$emit('updateHead')
                })
                .catch(() => {
                    this.$router.push({ name: 'students' })
                })
        },
        navigateToClassDetail(course) {
            this.$router.push({
                name: 'classes.detail',
                params: { uuid: course.uuid }
            })
        },
        handleNotes(index, row) {
            this.selectedClass = row
            this.showNotes = true
        },
        handleRemove(index, row) {
            this.$confirm({
                title: 'Confirm',
                message: 'Are you sure you want to remove this class?'
            })
                .then(() => {
                    return this.removeCourseFromStudent(row.id)
                })
                .then(() => {
                    this.$notify({
                        title: 'Success',
                        message: 'The class has been removed from the student!',
                        type: 'success',
                        offset: 75,
                        duration: 2000
                    })
                })
        },
        handleResetToken() {
            this.$confirm({
                title: 'Confirm',
                message: 'Are you sure you want to reset this students token?'
            }).then(() => {
                this.$confirm({
                    title: 'Confirm',
                    message:
                        'Would you like for this student to be notified with an email containing the refreshed token?',
                    resolveButtonLabel: 'Yes',
                    rejectButtonLabel: 'No'
                }).then(() => {
                    Student.$mutate({
                        graphql: 'resetToken',
                        variables: { uuid: this.student.uuid, send_email: true }
                    })
                }).catch(() => {
                    Student.$mutate({
                        graphql: 'resetToken',
                        variables: { uuid: this.student.uuid, send_email: false }
                    })
                })
            }).catch(() => {})
        },
        get,
        phoneFormat,
        apaFormat
    },
    watch: {
        uuid() {
            this.getStudent()
        }
    }
}
</script>

<style lang="stylus">
.student-detail-view
    .el-table .cell, .el-table th div
        padding-right: 5px

        .status-block
            margin-left: -5px
            margin-right: -5px

    .alert
        margin-top: 20px

.reset-padding
    padding-right: 25px

.reset-token
    margin-top: -5px
    margin-bottom: -5px
    position: absolute
    right: 10px
    top: 50%
    margin-top: -15px
    padding: 6px 10px

.table-buttons
    > *
        margin-left: 3px
</style>
